import { useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";

// ─── Sky animation keyframes ─────────────────────────────────────────────────
const SKY_CSS = `
  @keyframes sk-twinkle {
    0%, 100% { opacity: var(--so, 0.38); transform: scale(1); }
    50%       { opacity: var(--so2, 0.9); transform: scale(1.5); }
  }
  @keyframes sk-sun-rot   { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
  @keyframes sk-corona    { 0%, 100% { transform: scale(1); opacity: .6; } 50% { transform: scale(1.08); opacity: 1; } }
  @keyframes sk-ray-pulse { 0%, 100% { opacity: .14; } 50% { opacity: .42; } }
  @keyframes sk-flap      { 0%, 100% { transform: scaleY(1); } 50% { transform: scaleY(.45); } }
  @keyframes sk-aurora    { 0%, 100% { opacity: .05; transform: translateX(0); } 50% { opacity: .16; transform: translateX(-16px); } }
  @keyframes sk-cloud-bob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }
  @keyframes sk-moon-glow { 0%, 100% { opacity: .55; } 50% { opacity: .95; } }
  .sk-twinkle   { animation: sk-twinkle var(--sd, 3s) ease-in-out var(--dl, 0s) infinite; }
  .sk-sun-rot   { animation: sk-sun-rot 90s linear infinite; transform-origin: center; }
  .sk-corona    { animation: sk-corona 5s ease-in-out infinite; }
  .sk-ray-pulse { animation: sk-ray-pulse var(--rd, 8s) ease-in-out var(--dl, 0s) infinite; }
  .sk-flap      { animation: sk-flap var(--bd, .5s) ease-in-out var(--dl, 0s) infinite; transform-origin: center; }
  .sk-aurora    { animation: sk-aurora var(--ad, 9s) ease-in-out var(--dl, 0s) infinite; }
  .sk-cloud-bob { animation: sk-cloud-bob var(--cb, 8s) ease-in-out var(--dl, 0s) infinite; }
  .sk-moon-glow { animation: sk-moon-glow 6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) {
    .sk-twinkle, .sk-sun-rot, .sk-corona, .sk-ray-pulse, .sk-flap, .sk-aurora, .sk-cloud-bob, .sk-moon-glow { animation: none !important; }
  }
`;

const DAY_GRADIENT =
  "linear-gradient(178deg, #4aa8ef 0%, #6dbef6 11%, #95d3fb 25%, #bce8ff 41%, #d9f2ff 56%, #f0f8ff 72%, #fdf3e2 88%, #fffaf2 100%)";
const NIGHT_GRADIENT =
  "linear-gradient(178deg, #010613 0%, #030b1f 14%, #050f2b 28%, #061536 43%, #051028 60%, #030a1c 80%, #01060f 100%)";

// ─── Fluffy cloud SVGs (overlapping ellipses) ────────────────────────────────
const CloudA = ({ o = 0.9 }) => (
  <svg viewBox="0 0 280 116" width="280" height="116" aria-hidden="true">
    <ellipse cx="140" cy="84" rx="124" ry="26" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="92"  cy="64" rx="58"  ry="40" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="164" cy="58" rx="68"  ry="46" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="120" cy="44" rx="50"  ry="40" fill={`rgba(255,255,255,${Math.min(1, o + 0.05)})`} />
    <ellipse cx="176" cy="40" rx="40"  ry="32" fill={`rgba(255,255,255,${Math.min(1, o + 0.05)})`} />
    <ellipse cx="140" cy="96" rx="118" ry="13" fill="rgba(176,200,232,0.16)" />
  </svg>
);
const CloudB = ({ o = 0.85 }) => (
  <svg viewBox="0 0 200 84" width="200" height="84" aria-hidden="true">
    <ellipse cx="100" cy="62" rx="88" ry="18" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="66"  cy="46" rx="44" ry="29" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="124" cy="40" rx="52" ry="34" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="94"  cy="30" rx="36" ry="27" fill={`rgba(255,255,255,${Math.min(1, o + 0.05)})`} />
    <ellipse cx="100" cy="70" rx="84" ry="9"  fill="rgba(176,200,232,0.14)" />
  </svg>
);
const CloudC = ({ o = 0.75 }) => (
  <svg viewBox="0 0 160 68" width="160" height="68" aria-hidden="true">
    <ellipse cx="80" cy="50" rx="70" ry="15" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="54" cy="37" rx="36" ry="24" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="98" cy="31" rx="42" ry="28" fill={`rgba(255,255,255,${o})`} />
    <ellipse cx="76" cy="23" rx="30" ry="22" fill={`rgba(255,255,255,${Math.min(1, o + 0.07)})`} />
  </svg>
);

// ─── Drifting cloud — slow horizontal travel + gentle bob ────────────────────
const DriftCloud = ({ children, top, dur, delay = 0, bob = 8, opacity = 1 }) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return (
      <div className="absolute" style={{ top, left: "30%", opacity }} aria-hidden="true">
        {children}
      </div>
    );
  }
  return (
    <motion.div
      aria-hidden="true"
      className="absolute sk-cloud-bob"
      style={{ top, left: 0, opacity, willChange: "transform", "--cb": `${bob}s`, "--dl": `${(delay % 5).toFixed(1)}s` }}
      initial={{ x: "-22vw" }}
      animate={{ x: "122vw" }}
      transition={{ duration: dur, repeat: Infinity, ease: "linear", delay }}
    >
      {children}
    </motion.div>
  );
};

// ─── Sun with rotating spokes + pulsing corona ───────────────────────────────
const Sun = () => (
  <div aria-hidden="true" className="absolute" style={{ right: "9%", top: "12%" }}>
    <div className="sk-corona" style={{ position: "absolute", inset: -70, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(255,224,90,0.40) 0%, rgba(255,186,20,0.14) 46%, transparent 72%)",
      filter: "blur(10px)" }} />
    <div className="sk-sun-rot" style={{ position: "absolute", inset: -52, borderRadius: "50%" }}>
      {Array.from({ length: 12 }, (_, i) => i * 30).map((deg, i) => (
        <div key={deg} style={{ position: "absolute", left: "50%", top: "50%", width: 2, height: "50%",
          background: `rgba(255,213,60,${0.30 + (i % 3) * 0.12})`, transformOrigin: "top center",
          transform: `translateX(-50%) rotate(${deg}deg)`, filter: "blur(3px)" }} />
      ))}
    </div>
    <div style={{ width: 90, height: 90, borderRadius: "50%",
      background: "radial-gradient(circle at 40% 36%, #fff9d6 0%, #ffe766 28%, #ffcb12 58%, #ffa908 100%)",
      boxShadow: "0 0 44px rgba(255,210,50,0.80), 0 0 90px rgba(255,180,0,0.42), 0 0 150px rgba(255,150,0,0.20)" }} />
  </div>
);

// ─── God rays angled from the sun corner ─────────────────────────────────────
const GodRays = () => {
  const reduce = useReducedMotion();
  const rays = [
    { left: "64%", skew: -18, w: 50, dur: 13 },
    { left: "72%", skew: -12, w: 92, dur: 8 },
    { left: "80%", skew: -6,  w: 58, dur: 11 },
    { left: "88%", skew: 4,   w: 74, dur: 9 },
    { left: "94%", skew: 11,  w: 60, dur: 7 },
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {rays.map((r, i) => (
        <div
          key={i}
          className={reduce ? "absolute top-0" : "sk-ray-pulse absolute top-0"}
          style={{ left: r.left, width: r.w, height: "74%",
            background: "linear-gradient(180deg, rgba(255,236,166,0.55) 0%, transparent 100%)",
            transform: `skewX(${r.skew}deg)`, filter: "blur(14px)",
            opacity: reduce ? 0.2 : undefined, "--rd": `${r.dur}s`, "--dl": `${i * 0.7}s` }}
        />
      ))}
    </div>
  );
};

// ─── Bird silhouette + flock ─────────────────────────────────────────────────
const Bird = ({ size = 13 }) => (
  <svg width={size * 2.4} height={size} viewBox="0 0 28 14" aria-hidden="true">
    <path d="M14 8 C10 3 5 2 1 4" stroke="rgba(28,46,84,0.62)" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M14 8 C18 3 23 2 27 4" stroke="rgba(28,46,84,0.62)" strokeWidth="2" fill="none" strokeLinecap="round" />
  </svg>
);

const BirdFlock = ({ top, count = 5, size = 12, dir = "right", dur, delay = 0 }) => {
  const reduce = useReducedMotion();
  if (reduce) return null;
  const from = dir === "right" ? "-16vw" : "116vw";
  const to = dir === "right" ? "116vw" : "-16vw";
  const birds = Array.from({ length: count }, (_, i) => ({
    dx: 50 + ((i % 3) * 26 - 26) + (i > 2 ? -18 : 8),
    dy: 24 + ((i % 2) * 13 - 6) + (i > 3 ? 9 : 0),
    s: 0.7 + (i % 3) * 0.22,
    bd: (0.34 + (i % 5) * 0.07).toFixed(2),
    fl: (i * 0.08).toFixed(2),
  }));
  return (
    <motion.div
      aria-hidden="true"
      className="absolute"
      style={{ top, left: 0, willChange: "transform" }}
      initial={{ x: from }}
      animate={{ x: to, y: [0, -14, 9, -9, 0] }}
      transition={{
        x: { duration: dur, repeat: Infinity, ease: "linear", delay },
        y: { duration: dur / 5, repeat: Infinity, ease: "easeInOut" },
      }}
    >
      <div style={{ position: "relative", width: 130, height: 56 }}>
        {birds.map((b, i) => (
          <div key={i} className="sk-flap absolute"
            style={{ left: b.dx, top: b.dy, transform: `scale(${b.s})${dir === "left" ? " scaleX(-1)" : ""}`,
              "--bd": `${b.bd}s`, "--dl": `${b.fl}s` }}>
            <Bird size={size} />
          </div>
        ))}
      </div>
    </motion.div>
  );
};

// ─── Moon with craters, shadow + glowing halo ────────────────────────────────
const Moon = () => (
  <div aria-hidden="true" className="absolute" style={{ right: "10%", top: "9%" }}>
    <div className="sk-moon-glow" style={{ position: "absolute", inset: -46, borderRadius: "50%",
      background: "radial-gradient(circle, rgba(184,216,255,0.30) 0%, transparent 70%)", filter: "blur(10px)" }} />
    <div style={{ position: "relative", width: 98, height: 98, borderRadius: "50%",
      background: "radial-gradient(circle at 36% 32%, #f4faff 0%, #dceeff 32%, #c3ddf7 62%, #a5c9ef 100%)",
      boxShadow: "0 0 30px rgba(184,216,255,0.48), 0 0 64px rgba(184,216,255,0.18), inset -5px -5px 10px rgba(96,142,212,0.16)" }}>
      <div style={{ position: "absolute", width: 19, height: 19, borderRadius: "50%", top: "27%", left: "21%",
        background: "radial-gradient(circle, rgba(150,184,228,0.45) 0%, transparent 70%)" }} />
      <div style={{ position: "absolute", width: 12, height: 12, borderRadius: "50%", top: "53%", left: "57%",
        background: "radial-gradient(circle, rgba(150,184,228,0.38) 0%, transparent 70%)" }} />
      <div style={{ position: "absolute", width: 8, height: 8, borderRadius: "50%", top: "21%", left: "61%",
        background: "radial-gradient(circle, rgba(150,184,228,0.32) 0%, transparent 70%)" }} />
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%",
        background: "radial-gradient(circle at 70% 66%, rgba(72,104,176,0.20) 0%, transparent 56%)" }} />
    </div>
  </div>
);

// ─── Aurora / nebula wisps ───────────────────────────────────────────────────
const Aurora = () => {
  const wisps = [
    { top: "10%", left: "-12%", w: "72%", h: 150, c1: "rgba(99,102,241,0.11)", c2: "rgba(45,212,191,0.06)", dur: 9, dl: 0 },
    { top: "20%", left: "28%",  w: "62%", h: 110, c1: "rgba(168,85,247,0.09)", c2: "rgba(99,102,241,0.05)", dur: 12, dl: 3 },
    { top: "5%",  left: "38%",  w: "54%", h: 90,  c1: "rgba(45,212,191,0.08)", c2: "rgba(99,102,241,0.04)", dur: 11, dl: 5 },
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {wisps.map((w, i) => (
        <div key={i} className="sk-aurora absolute"
          style={{ top: w.top, left: w.left, width: w.w, height: w.h,
            background: `linear-gradient(90deg, ${w.c1} 0%, ${w.c2} 50%, transparent 100%)`,
            filter: "blur(30px)", "--ad": `${w.dur}s`, "--dl": `${w.dl}s` }} />
      ))}
    </div>
  );
};

// ─── Shooting star — glowing head + fading trail, fires periodically ─────────
const ShootingStar = ({ top, right, delay, gap }) => {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <motion.div
      aria-hidden="true"
      className="absolute"
      style={{ top, right, willChange: "transform, opacity" }}
      initial={{ x: 0, y: 0, opacity: 0 }}
      animate={{ x: [-10, -460], y: [0, 265], opacity: [0, 1, 0.85, 0] }}
      transition={{ duration: 1.7, repeat: Infinity, repeatDelay: gap, ease: [0.25, 0, 0.75, 1], times: [0, 0.08, 0.62, 1], delay }}
    >
      <div style={{ position: "absolute", right: 0, top: -3, width: 6, height: 6, borderRadius: "50%",
        background: "radial-gradient(circle, #ffffff 0%, rgba(200,230,255,0.85) 50%, transparent 80%)",
        boxShadow: "0 0 9px 3px rgba(200,230,255,0.7)" }} />
      <div style={{ width: 170, height: 2, borderRadius: 999,
        background: "linear-gradient(90deg, transparent 0%, rgba(180,220,255,0.28) 32%, rgba(220,240,255,0.68) 72%, #ffffff 100%)",
        transform: "rotate(-30deg)", transformOrigin: "right center", filter: "blur(1.4px)",
        boxShadow: "0 0 6px rgba(200,230,255,0.5)" }} />
    </motion.div>
  );
};

// ─── Twinkling starfield ─────────────────────────────────────────────────────
const Stars = () => {
  const reduce = useReducedMotion();
  const stars = useMemo(
    () =>
      Array.from({ length: 130 }, (_, i) => {
        const tier = i < 9 ? "bright" : i < 32 ? "mid" : "faint";
        const size = tier === "bright" ? 3.0 + (i % 3) * 0.7 : tier === "mid" ? 1.9 + (i % 4) * 0.4 : 0.8 + (i % 5) * 0.42;
        const colors = ["#ffffff", "#e8f4ff", "#cce6ff", "#fff6ec", "#dceaff"];
        return {
          x: ((i * 137.508 + i * 3.14) % 100).toFixed(2),
          y: ((i * 61.803 + i * 2.71) % 82).toFixed(2),
          size,
          color: colors[i % colors.length],
          dur: (2.0 + (i % 11) * 0.42).toFixed(1),
          delay: ((i * 0.41) % 6.5).toFixed(1),
          op: tier === "bright" ? 0.78 + (i % 3) * 0.07 : tier === "mid" ? 0.45 + (i % 5) * 0.08 : 0.2 + (i % 7) * 0.09,
          glow: tier === "bright",
        };
      }),
    []
  );
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {stars.map((s, i) => (
        <span
          key={i}
          className={reduce ? "absolute rounded-full" : "sk-twinkle absolute rounded-full"}
          style={{
            left: `${s.x}%`, top: `${s.y}%`, width: s.size, height: s.size,
            opacity: reduce ? s.op : undefined,
            "--so": s.op, "--so2": Math.min(1, s.op + 0.5), "--sd": `${s.dur}s`, "--dl": `${s.delay}s`,
            background: s.color,
            boxShadow: s.glow ? `0 0 ${s.size * 4}px rgba(200,230,255,0.75)` : "none",
          }}
        />
      ))}
    </div>
  );
};

// ─── Drifting wispy night clouds (low opacity, for depth) ────────────────────
const NightClouds = () => {
  const reduce = useReducedMotion();
  if (reduce) return null;
  const clouds = [
    { top: "16%", dur: 150, delay: 0, o: 0.16 },
    { top: "26%", dur: 180, delay: -55, o: 0.11 },
    { top: "8%", dur: 165, delay: -100, o: 0.14 },
  ];
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden">
      {clouds.map((c, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{ top: c.top, left: 0, opacity: c.o, filter: "blur(2px)" }}
          initial={{ x: "-24vw" }}
          animate={{ x: "124vw" }}
          transition={{ duration: c.dur, repeat: Infinity, ease: "linear", delay: c.delay }}
        >
          <CloudA o={0.55} />
        </motion.div>
      ))}
    </div>
  );
};

// ─── Full sky scene ──────────────────────────────────────────────────────────
export const SkyScene = ({ mode = "night" }) => {
  const reduce = useReducedMotion();
  const day = mode === "day";

  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <style>{SKY_CSS}</style>

      {/* Base sky gradient */}
      <div className="absolute inset-0" style={{ background: day ? DAY_GRADIENT : NIGHT_GRADIENT }} />

      {day ? (
        <>
          {/* Milky daytime haze near horizon */}
          <GodRays />

          {/* Three cloud depth layers */}
          {!reduce && (
            <>
              {/* far — small, faint, slow */}
              <DriftCloud top="5%" dur={150} delay={0} bob={11} opacity={0.40}><CloudC o={0.42} /></DriftCloud>
              <DriftCloud top="11%" dur={170} delay={-50} bob={13} opacity={0.36}><CloudB o={0.40} /></DriftCloud>
              <DriftCloud top="3%" dur={185} delay={-110} bob={9} opacity={0.32}><CloudC o={0.38} /></DriftCloud>
              {/* mid */}
              <DriftCloud top="9%" dur={108} delay={0} bob={10} opacity={0.66}><CloudA o={0.62} /></DriftCloud>
              <DriftCloud top="15%" dur={122} delay={-35} bob={12} opacity={0.60}><CloudB o={0.58} /></DriftCloud>
              <DriftCloud top="20%" dur={114} delay={-72} bob={9} opacity={0.55}><CloudC o={0.56} /></DriftCloud>
              {/* near — large, opaque, fast */}
              <DriftCloud top="4%" dur={78} delay={0} bob={15} opacity={0.92}><CloudA o={0.88} /></DriftCloud>
              <DriftCloud top="13%" dur={84} delay={-26} bob={16} opacity={0.86}><CloudB o={0.84} /></DriftCloud>
              <DriftCloud top="22%" dur={72} delay={-50} bob={13} opacity={0.80}><CloudA o={0.80} /></DriftCloud>
            </>
          )}

          <Sun />

          {/* Bird flocks */}
          <BirdFlock top="24%" count={6} size={12} dir="right" dur={36} delay={0} />
          <BirdFlock top="32%" count={4} size={9} dir="left" dur={44} delay={9} />
          <BirdFlock top="19%" count={7} size={10} dir="right" dur={52} delay={22} />

          {/* Warm horizon glow */}
          <div className="absolute inset-x-0 bottom-0" style={{ height: "40%",
            background: "linear-gradient(180deg, transparent 0%, rgba(255,222,140,0.12) 58%, rgba(255,184,90,0.20) 100%)" }} />
        </>
      ) : (
        <>
          <Aurora />
          <Stars />
          <NightClouds />
          <Moon />

          {/* Shooting stars — staggered so one streaks roughly every ~8s */}
          <ShootingStar top="7%" right="24%" delay={2} gap={22} />
          <ShootingStar top="12%" right="46%" delay={11} gap={26} />
          <ShootingStar top="5%" right="64%" delay={19} gap={24} />
        </>
      )}

      {/* Fade scene into the page background near the bottom */}
      <div className="absolute inset-x-0 bottom-0 h-64" style={{
        background: "linear-gradient(180deg, transparent 0%, var(--bg-0) 100%)" }} />
    </div>
  );
};
